import type { TimelineSegment } from '@api/types';

function isPlaylistSegment(segment: TimelineSegment): boolean {
  return segment.status === 'complete' || segment.status === 'in-progress';
}

export function sortSegmentsChronological(segments: TimelineSegment[]): TimelineSegment[] {
  return [...segments].sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
  );
}

export function getSegmentPlaybackOffsetSeconds(
  segments: TimelineSegment[],
  selectedSegmentId: string,
): number {
  const ordered = sortSegmentsChronological(segments);
  let offsetSeconds = 0;
  for (const segment of ordered) {
    if (segment.id === selectedSegmentId) return offsetSeconds;
    if (isPlaylistSegment(segment)) offsetSeconds += segment.durationSeconds;
  }
  return 0;
}

/**
 * Maps virtual playlist offset (seconds from start of concatenated segments) to wall-clock ms.
 */
export function wallClockMsFromPlaylistOffset(
  segments: TimelineSegment[],
  offsetSeconds: number,
): number | null {
  const ordered = sortSegmentsChronological(segments).filter(isPlaylistSegment);
  if (ordered.length === 0) return null;

  let remaining = offsetSeconds;
  if (remaining <= 0) {
    return new Date(ordered[0].startTime).getTime();
  }

  for (const seg of ordered) {
    const dur = seg.durationSeconds;
    if (remaining <= dur) {
      return new Date(seg.startTime).getTime() + remaining * 1000;
    }
    remaining -= dur;
  }

  return new Date(ordered[ordered.length - 1].endTime).getTime();
}

export interface PlaylistSeekResult {
  offsetSeconds: number;
  segmentId: string | null;
}

/**
 * Maps a wall-clock instant to virtual playlist time and the segment that should be active.
 * Gaps snap to the next segment's start in playlist space; time after the last segment snaps to its end.
 */
export function playlistSeekFromWallClockMs(
  segments: TimelineSegment[],
  wallMs: number,
): PlaylistSeekResult {
  const ordered = sortSegmentsChronological(segments).filter(isPlaylistSegment);
  if (ordered.length === 0) {
    return { offsetSeconds: 0, segmentId: null };
  }

  let playlistOffset = 0;
  for (const seg of ordered) {
    const start = new Date(seg.startTime).getTime();
    const end = new Date(seg.endTime).getTime();
    if (wallMs < start) {
      return { offsetSeconds: playlistOffset, segmentId: seg.id };
    }
    if (wallMs <= end) {
      return {
        offsetSeconds: playlistOffset + (wallMs - start) / 1000,
        segmentId: seg.id,
      };
    }
    playlistOffset += seg.durationSeconds;
  }

  const last = ordered[ordered.length - 1];
  return { offsetSeconds: playlistOffset, segmentId: last.id };
}

const END_EPSILON_S = 0.05;

/**
 * Resolves a virtual playlist offset (seconds) to a safe `HTMLMediaElement.currentTime`.
 * After `MANIFEST_PARSED`, HLS often reports a **partial** `video.duration` until the full
 * VOD span is known; clamping with that value would pin deep seeks to an early timestamp.
 */
export function resolveTimelineSeekTargetSeconds(
  offsetSeconds: number,
  mediaDurationSeconds: number | null,
): number {
  const safeOffset = Math.max(0, offsetSeconds);
  if (
    mediaDurationSeconds == null ||
    !Number.isFinite(mediaDurationSeconds) ||
    mediaDurationSeconds <= 0
  ) {
    return safeOffset;
  }
  const nearEnd = Math.max(0, mediaDurationSeconds - END_EPSILON_S);
  if (safeOffset <= nearEnd) {
    return Math.min(safeOffset, nearEnd);
  }
  return safeOffset;
}

/** Sum of `durationSeconds` for segments that appear in the virtual playlist. */
export function playlistMetaTotalSeconds(segments: TimelineSegment[]): number {
  return sortSegmentsChronological(segments)
    .filter(isPlaylistSegment)
    .reduce((acc, s) => acc + s.durationSeconds, 0);
}

/**
 * Maps `video.currentTime` into the same virtual offset space as {@link wallClockMsFromPlaylistOffset}
 * when the media element's reported duration is **complete** (≥ meta span) but differs from API
 * metadata (e.g. EXTINF vs DB). Does **not** rescale when duration still looks partial (would map
 * `currentTime ≈ duration` to the wrong end of the day).
 */
export function offsetForWallClockMapping(
  segments: TimelineSegment[],
  videoCurrentTime: number,
  videoDurationSeconds: number | null,
): number {
  const t = Math.max(0, videoCurrentTime);
  const meta = playlistMetaTotalSeconds(segments);
  if (meta <= 0) return t;
  const dur =
    videoDurationSeconds != null &&
    Number.isFinite(videoDurationSeconds) &&
    videoDurationSeconds > 0
      ? videoDurationSeconds
      : null;
  if (dur == null) return t;

  const durationLooksComplete = dur >= meta * 0.98;
  const relDrift = Math.abs(meta - dur) / Math.max(meta, dur);
  if (!durationLooksComplete || relDrift < 0.02) return t;

  const scaled = (t / dur) * meta;
  return Math.min(Math.max(0, scaled), meta);
}
